import { useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useModalFocus } from "@/hooks/useModalFocus";
import "./infrastructure.css";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => unknown;
  onCancel: () => void;
  danger?: boolean;
}
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  danger = false,
}: ConfirmDialogProps) {
  const id = useId(),
    ref = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  useModalFocus(ref, open, () => {
    if (!busy) onCancel();
  });
  if (!open) return null;
  return createPortal(
    <div
      className="ui-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        tabIndex={-1}
        className="ui-dialog"
      >
        <h2 id={id}>{title}</h2>
        <div>{children}</div>
        {error && <p role="alert">{error}</p>}
        <div className="ui-dialog-actions">
          <button data-autofocus disabled={busy} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            className={danger ? "btn btn-danger" : "btn btn-primary"}
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setError("");
              void Promise.resolve()
                .then(onConfirm)
                .then(onCancel)
                .catch((reason: unknown) =>
                  setError(
                    reason instanceof Error ? reason.message : "The change could not be completed.",
                  ),
                )
                .finally(() => setBusy(false));
            }}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
